import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { Droplets } from "lucide-react";
import { Spinner } from "../board/Spinner";
import { useAuth } from "./AuthProvider";
import { friendlyAuthError } from "./friendlyAuthError";

export function SignIn() {
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetPending, setResetPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await auth.signIn(email.trim(), password);
      // No navigation here: AuthProvider's onAuthStateChanged flips the whole app to the
      // scoped board. This component simply unmounts.
    } catch (err) {
      setError(friendlyAuthError(err));
      setPending(false); // stays mounted on failure, so the button must be usable again
    }
  };

  const sendReset = async (e: FormEvent) => {
    e.preventDefault();
    setResetError(null);
    setResetPending(true);
    try {
      await auth.sendPasswordReset(resetEmail.trim());
    } catch (err) {
      // Deliberately swallowed: auth/user-not-found and auth/invalid-email must look exactly
      // like success, or this becomes an enumeration oracle for staff addresses. Only a rate
      // limit — which says nothing about whether the account exists — is surfaced.
      if ((err as { code?: string })?.code === "auth/too-many-requests") {
        setResetError("Too many attempts. Please wait a minute and try again.");
        setResetPending(false);
        return;
      }
    }
    setResetSent(true);
    setResetPending(false);
  };

  // Fully closes the panel: toggle reappears, any in-progress state is discarded. Clearing
  // resetPending here is cosmetic — an in-flight sendReset promise still resolves and calls
  // setResetSent(true)/setResetPending(false) after close — but stops a stale "pending"
  // flag from lingering if the panel is reopened before that promise settles.
  const closeReset = () => {
    setResetOpen(false);
    setResetEmail("");
    setResetSent(false);
    setResetError(null);
    setResetPending(false);
  };

  // Lets someone who mistyped their address correct it without leaving the panel.
  const retryReset = () => {
    setResetSent(false);
    setResetError(null);
  };

  return (
    <div className="min-h-screen grid place-items-center p-6" style={{ background: "#eef2f4" }}>
      <div className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 shadow-lg">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: "#0fa3b1" }}>
            <Droplets size={20} />
          </div>
          <h1 className="text-lg font-bold text-slate-800">Staff sign-in</h1>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block text-sm font-medium text-slate-700">
            Email
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              autoComplete="username" required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Password
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password" required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
            />
          </label>

          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

          {/* src/apps/CLAUDE.md async-feedback rule: disabled + inline spinner while in flight,
              with the label swapped for the spinner so the button does not resize. */}
          <button
            type="submit" disabled={pending}
            className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-semibold text-white shadow disabled:opacity-60"
            style={{ background: "#0fa3b1" }}
          >
            {pending ? <Spinner /> : "Sign in"}
          </button>
        </form>

        {!resetOpen && (
          <>
            <button type="button" onClick={() => setResetOpen(true)} className="w-full text-sm text-slate-500 hover:text-slate-700">
              Forgot password?
            </button>
            <Link to="/signup" className="block w-full text-center text-sm text-slate-500 hover:text-slate-700">
              Create an account
            </Link>
          </>
        )}

        {resetOpen && (
          <div className="space-y-2 rounded-xl bg-slate-50 p-3">
            {resetSent ? (
              <>
                <p className="text-sm text-slate-600">If that address has an account, we've sent a reset link.</p>
                <div className="flex gap-3">
                  <button type="button" onClick={retryReset} className="text-sm text-slate-500 underline hover:text-slate-700">
                    Send to a different email
                  </button>
                  <button type="button" onClick={closeReset} className="text-sm text-slate-500 underline hover:text-slate-700">
                    Back to sign in
                  </button>
                </div>
              </>
            ) : (
              // Its own <form>, separate from the sign-in form above, so pressing Enter in
              // "Reset email" submits THIS form (calling sendReset) instead of the sign-in
              // form. Nesting one <form> inside another is invalid HTML and wouldn't fix that.
              // noValidate: browser format-checking on type="email" would silently swallow
              // the submit for a malformed address before our handler ever runs, which would
              // make this field behave differently from a valid-but-unknown address — the
              // exact distinction the identical-confirmation design exists to avoid. Firebase's
              // own auth/invalid-email response goes through the same swallowed-error path.
              <form onSubmit={(e) => void sendReset(e)} className="space-y-2" noValidate>
                <label className="block text-sm font-medium text-slate-700">
                  Reset email
                  <input
                    type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-slate-900"
                  />
                </label>
                {resetError && <p role="alert" className="text-sm text-red-600">{resetError}</p>}
                <div className="flex gap-2">
                  <button
                    type="submit" disabled={resetPending}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-60"
                  >
                    {resetPending ? <Spinner /> : "Send reset link"}
                  </button>
                  <button
                    type="button" onClick={closeReset}
                    className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
